import { Modal } from "antd";
import { toast } from "react-toastify";

// The one place the "your trial/subscription has ended" dialog is built.
// Invoked from the global axios interceptor when the backend refuses a
// mutating action with its structured expiry 403, so EVERY blocked action —
// fetching notices, generating a response, adding a client — produces the
// same dialog without any per-page wiring.

let dialogOpen = false;
let modalInstance = null;

// Imperative antd modals are NOT part of the route tree, so they survive
// soft navigation — including the navigation that ends an impersonation
// session. Without this, a dialog raised while acting as an expired tenant
// stayed open on the ADMIN's own screen after "Stop impersonating".
// Called from the impersonation teardown path on every identity switch.
export const dismissSubscriptionExpiredDialog = () => {
  try {
    modalInstance?.destroy();
  } catch {
    /* already closed */
  }
  modalInstance = null;
  dialogOpen = false;
};

// True when an error is the backend's structured expiry refusal. For catch
// handlers that want to stay quiet when the upgrade dialog is already telling
// the user what happened — an expired plan is a handled state, not an error
// worth logging or toasting.
//
// Handles THREE shapes, because apiService.parseErrorMessage re-throws a
// PLAIN STRING (the detail's message) for many 4xx responses, so page-level
// catches may receive the axios error, an Error, or just the message text.
const _EXPIRY_MESSAGE_MARKS = [
  "Your free trial has ended",
  "Your subscription has ended",
];

export const isSubscriptionExpiredError = (err) => {
  const code = err?.response?.data?.detail?.code;
  if (code === "trial_expired" || code === "subscription_expired") return true;
  const text =
    typeof err === "string" ? err : typeof err?.message === "string" ? err.message : "";
  return _EXPIRY_MESSAGE_MARKS.some((mark) => text.includes(mark));
};

export const showSubscriptionExpiredDialog = ({
  message,
  shopUrl = "/shop",
  code = "trial_expired",
} = {}) => {
  // A blocked screen often fires several requests at once (a fetch plus a
  // status poll); one dialog is information, five stacked ones are a bug.
  if (dialogOpen) return;
  dialogOpen = true;

  // The page whose action was refused usually has its own catch handler that
  // fires a generic red error toast ("Failed to queue all clients..."). That
  // toast lands right AFTER this dialog opens — the page's catch runs on the
  // same rejected promise — and dozens of pages have one, so silencing them
  // at the source would be an endless chase. This dialog IS the error
  // surface: sweep the toasts a few times as they land. The window is short
  // enough that an unrelated toast is very unlikely to be caught in it.
  const sweep = () => toast.dismiss();
  sweep();
  setTimeout(sweep, 120);
  setTimeout(sweep, 450);

  const isTrial = code === "trial_expired";
  const title = isTrial ? "Your free trial has ended" : "Your subscription has ended";
  const body =
    message ||
    (isTrial
      ? "Your free trial has ended. Pick a plan to keep working."
      : "Your subscription has ended. Renew to keep working.");

  modalInstance = Modal.confirm({
    title: (
      <span style={{ fontWeight: 700, fontSize: 17, color: "#1e2a63" }}>
        {title}
      </span>
    ),
    icon: null,
    centered: true,
    width: 440,
    content: (
      <div style={{ paddingTop: 4 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "4px auto 16px",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #2746b2, #3656b6)",
            boxShadow: "0 8px 20px rgba(39, 70, 178, 0.35)",
            fontSize: 30,
          }}
        >
          <span role="img" aria-label="crown">
            👑
          </span>
        </div>
        <p style={{ color: "#475069", fontSize: 14, textAlign: "center", marginBottom: 6 }}>
          {body}
        </p>
        <p style={{ color: "#8a91a8", fontSize: 12.5, textAlign: "center", margin: 0 }}>
          You can keep viewing everything. Upgrading restores fetching,
          response generation and client management instantly.
        </p>
      </div>
    ),
    okText: "Upgrade Now",
    okButtonProps: {
      className: "exempt-from-readonly",
      style: {
        background: "linear-gradient(90deg, #2746b2, #3656b6)",
        borderColor: "#2746b2",
        fontWeight: 600,
        boxShadow: "0 4px 12px rgba(39, 70, 178, 0.3)",
      },
    },
    cancelText: "Continue viewing",
    cancelButtonProps: { className: "exempt-from-readonly" },
    onOk: () => {
      dialogOpen = false;
      modalInstance = null;
      // Hard navigation on purpose: the upgrade flow must start clean even
      // if the current page is mid-error.
      window.location.href = shopUrl || "/shop";
    },
    onCancel: () => {
      dialogOpen = false;
      modalInstance = null;
    },
  });
};
